import React from "react";
import { Space, Tag, Typography } from "antd";
import { DependencyStep, EditDiff, hasEdit, plural } from "./dependencyActions";
import { isForeignAddress } from "./pending";

const { Text } = Typography;

const isMaps = a => a.type === "maps";

/** « Rue X 12, 1870 Monthey » (postal) or the location string (type maps). */
export const addressLabel = a => {
  if (isMaps(a)) return a.location || a.name || a._id;
  const line1 = [a.street, a.number].filter(Boolean).join(" ");
  const line2 = [a.zip, a.city].filter(Boolean).join(" ");
  const parts = [line1, line2].filter(Boolean);
  if (a.name && !parts.length) return a.name;
  return parts.join(", ") || a.name || a._id;
};

const ownerOf = a => (a.owner && a.owner.name) || "?";

const POSTAL_FIELDS = [
  { key: "name", label: "Nom", kind: "text" },
  { key: "street", label: "Rue", kind: "text" },
  { key: "number", label: "Numéro", kind: "text" },
  { key: "zip", label: "NPA", kind: "text" },
  { key: "city", label: "Localité", kind: "text" },
  { key: "complement", label: "Complément", kind: "text" },
  { key: "description", label: "Description", kind: "text" },
  {
    key: "availableForBilling",
    label: "disponible pour la facturation",
    kind: "boolean",
  },
];
const MAPS_FIELDS = [
  { key: "name", label: "Nom", kind: "text" },
  { key: "location", label: "Lieu", kind: "text" },
];
const editFields = a => (isMaps(a) ? MAPS_FIELDS : POSTAL_FIELDS);

const usageLine = a => {
  const u = a.usage || {};
  const parts = [
    plural(u.prestations, "prestation", "prestations"),
    plural(u.bills, "facture", "factures"),
  ];
  if (Array.isArray(u.defaultOf) && u.defaultOf.length)
    parts.push(`adresse par défaut de : ${u.defaultOf.join(", ")}`);
  if (u.principals) parts.push(plural(u.principals, "mandant", "mandants"));
  return parts.join(" · ");
};

/** « Adresse (avant) »: label, type, owner, shared-to, usage. */
const renderBefore = (a, ctx) => (
  <>
    <Space size={6} wrap>
      <Text strong>{addressLabel(a)}</Text>
      <Tag>{isMaps(a) ? "lieu" : "postale"}</Tag>
      {a.external && <Tag>externe</Tag>}
    </Space>
    {a.name && addressLabel(a) !== a.name && (
      <Text type="secondary">{a.name}</Text>
    )}
    {a.complement && <Text type="secondary">{a.complement}</Text>}
    <Text type="secondary">
      propriétaire : {ownerOf(a)}
      {a.owner && a.owner.isTarget ? " (institution qui reçoit la fusion)" : ""}
    </Text>
    {(a.sharedTo || []).length > 0 && (
      <Text type="secondary">
        partagée vers : {a.sharedTo.map(s => s.name).join(", ")}
      </Text>
    )}
    <Text type="secondary">{usageLine(a)}</Text>
    {!isMaps(a) && a.availableForBilling === false && (
      <Text type="secondary">non disponible pour la facturation</Text>
    )}
  </>
);

/** Shared-to once the sources are replaced by the target (once, deduplicated). */
const sharedAfter = (a, ctx) => {
  const out = [];
  let hit = false;
  for (const s of a.sharedTo || []) {
    if (ctx.cluster.has(String(s._id))) hit = true;
    else if (!out.includes(s.name)) out.push(s.name);
  }
  const ownerMoves = a.owner && (a.owner.isSource || a.owner.isTarget);
  if (hit && !ownerMoves) out.unshift(ctx.targetName);
  return out;
};

/** « Après »: computed from the decision — owner/shared-to (keep), or « fusionnée dans … », plus the edits. */
const renderAfter = (a, d, ctx) => {
  if (d.action === "merge") {
    const s = d.into && ctx.byId.get(d.into);
    if (!s) return <Text type="danger">choisir l'adresse qui reste</Text>;
    const u = a.usage || {};
    return (
      <Space direction="vertical" size={2}>
        <Text>
          fusionnée dans : <Text strong>{addressLabel(s)}</Text>
        </Text>
        <Text type="secondary">
          supprimée, ses {plural(u.prestations, "prestation", "prestations")} /{" "}
          {plural(u.bills, "facture", "factures")} pointent vers elle
        </Text>
      </Space>
    );
  }
  const ownerMoves = a.owner && (a.owner.isSource || a.owner.isTarget);
  const shared = sharedAfter(a, ctx);
  const losers = [...ctx.byId.values()].filter(
    x => x._id !== a._id && ctx.map[x._id] && ctx.map[x._id].into === a._id,
  );
  return (
    <Space direction="vertical" size={2}>
      <Text>
        propriétaire :{" "}
        <Text strong>{ownerMoves ? ctx.targetName : ownerOf(a)}</Text>
        {!ownerMoves && <Text type="secondary"> (hors fusion, inchangé)</Text>}
      </Text>
      {shared.length > 0 && (
        <Text type="secondary">partagée vers : {shared.join(", ")}</Text>
      )}
      {losers.length > 0 && (
        <Text type="secondary">
          reçoit : {losers.map(l => addressLabel(l)).join(", ")}
        </Text>
      )}
      {hasEdit(d) && (
        <EditDiff fields={editFields(a)} original={a} edit={d.edit} />
      )}
    </Space>
  );
};

/** Older previews only carry the ownership matrix: same rows, action keep, no groups. */
const fallbackItems = section =>
  (section.matrix || []).map(m => ({
    _id: m._id,
    type: m.type,
    name: m.name || "",
    street: m.street || "",
    number: m.number || "",
    zip: m.zip || "",
    city: m.city || "",
    complement: "",
    description: "",
    location: m.location || "",
    availableForBilling: m.availableForBilling,
    external: m.external,
    owner: m.owner,
    sharedTo: m.sharedTo || [],
    usage: { prestations: m.prestations, bills: m.bills, defaultOf: [] },
    groupKey: null,
  }));

const SPEC = {
  key: "addresses",
  idsKey: "addressIds",
  alert:
    "Chaque adresse des établissements fusionnés est listée avec son état aujourd'hui (avant), ce qu'elle deviendra (après) et l'action appliquée. Les doublons détectés exigent une décision : garder séparées ou fusionner.",
  note: null,
  empty: "Aucune adresse concernée.",
  noOthers: "Toutes les adresses concernées font partie d'un doublon.",
  groupTitle: (g, n) =>
    `Doublon probable : ${g.label} — ${plural(n, "adresse", "adresses")}`,
  othersTitle: n => `Autres adresses — ${n}`,
  beforeTitle: "Adresse (avant)",
  radioKeep: "Rattacher telle quelle",
  radioMerge: "Fusionner dans →",
  radioEdit: "Modifier",
  selectPlaceholder: "Choisir l'adresse qui reste",
  alreadyMerged: "déjà fusionnée",
  noCandidate: "aucune autre adresse du même type",
  chainNote: "cette adresse est elle-même fusionnée — choisissez-en une autre",
  labelOf: addressLabel,
  ownerOf,
  editFields,
  candidates: (a, all) => all.filter(x => x.type === a.type),
  // owner neither a source nor the target: only « Rattacher telle quelle » (server readiness refuses the rest)
  isForeign: isForeignAddress,
  fallbackItems,
  renderBefore,
  renderAfter,
};

/**
 * Step « 3. Adresses » — every address the merge touches, before → after, with its action:
 * keep (« Rattacher telle quelle »), merge into another address of the result, or edit.
 * Near-duplicate groups (server detection) require an explicit decision for every member.
 */
export function StepAddresses(props) {
  return <DependencyStep spec={SPEC} {...props} />;
}
